import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useMemo,
  useState,
} from 'react'
import { Popover } from '@mantine/core'
import { TextInput } from '@percent/lemonade'

import { ClearButton } from '../clear-button/clear-button'
import {
  is12hourFormat,
  convertTo12HourFormat,
  convertTo24HourFormat,
  formatTime,
  generateTimeOptions,
} from './helpers/helpers'
import type { TimePickerProps } from './time-picker.types'
import styles from './time-picker.module.scss'

const MAX_LENGTH = 8

export function TimePicker({
  fieldName,
  handleChange,
  handleBlur,
  error,
  value,
  disabled,
  handleFieldClear,
  withinPopup,
  inputPlaceholder,
  'data-testid': dataTestid,
}: Readonly<TimePickerProps>) {
  const is12HrsFormat = is12hourFormat()

  const [optionsDropdownOpen, setOptionsDropdownOpen] = useState(false)

  const timeOptions = useMemo(
    () => generateTimeOptions(is12HrsFormat, 15),
    [is12HrsFormat]
  )

  const handleTimeChange = (e: ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value.replace(/[^0-9amp:]/gi, '')

    if (newValue.length > MAX_LENGTH) {
      newValue = newValue.slice(0, MAX_LENGTH)
    }

    handleChange(newValue)
  }

  const formatAndApplyValue = () => {
    if (value?.length) {
      const formattedTime = formatTime(value, is12HrsFormat)
      const localizedTime = is12HrsFormat
        ? convertTo12HourFormat(formattedTime)
        : convertTo24HourFormat(formattedTime)

      handleChange(localizedTime)
    }
  }

  const handleFieldBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    formatAndApplyValue()

    if (value?.length) {
      setTimeout(() => {
        handleBlur(e)
      }, 1)
    } else {
      handleBlur(e)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      formatAndApplyValue()
      setOptionsDropdownOpen(false)
      e.currentTarget.blur()
    }
  }

  const handleTimeSelect = (time: string) => {
    handleChange(time)
    setOptionsDropdownOpen(false)
  }

  const placeholder24Format = inputPlaceholder || 'hh:mm'
  /**
   ** NOTE: `is12HrsFormat` will be true only for locales that use the 12-hour clock (e.g. en-US).
   * For those we don't need translated value for placeholder,
   * so we keep a static English placeholder ('hh:mm aa'), since AM/PM is universally understood there.
   */
  const placeholder = is12HrsFormat ? 'hh:mm aa' : placeholder24Format

  return (
    <Popover
      opened={optionsDropdownOpen}
      onChange={() => setOptionsDropdownOpen(false)}
      closeOnClickOutside
      position="bottom-start"
      offset={8}
      width={120}
      withinPortal={!withinPopup}
      classNames={{
        dropdown: styles.Dropdown,
      }}
    >
      <Popover.Target>
        <div className={styles.InputWrapper}>
          <TextInput
            type="text"
            name={fieldName}
            value={value || ''}
            status={error ? 'danger' : 'default'}
            onChange={handleTimeChange}
            onBlur={handleFieldBlur}
            placeholder={placeholder}
            maxLength={MAX_LENGTH}
            disabled={disabled}
            autoComplete="off"
            onKeyDown={handleKeyDown}
            onClick={() => setOptionsDropdownOpen(true)}
            icon="clock"
            borderRadiiSize="large"
            data-testid={dataTestid}
          />
          {!!value && !!handleFieldClear && (
            <div className={styles.ClearBtnWrapper}>
              <ClearButton
                handleClick={() => handleFieldClear()}
                testId={`clear-${fieldName}`}
              />
            </div>
          )}
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <ul className={styles.OptionsList}>
          {timeOptions.map((time) => (
            <li className={styles.OptionItem} key={time}>
              <button
                className={styles.OptionItemButton}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTimeSelect(time)}
              >
                {time}
              </button>
            </li>
          ))}
        </ul>
      </Popover.Dropdown>
    </Popover>
  )
}
